import AutoOpenModal from '@/components/AutoOpenModal';
import TransactionNumberInput from '@/components/cadastre/TransactionNumberInput.vue';
import LineChartControl from '@/components/common/LineChartControl.vue';
import MultiSelectControl from '@/components/common/MultiSelectControl.vue';
import PieChartControl from '@/components/common/PieChartControl.vue';
import ReloadContainer from '@/components/common/ReloadContainer';
import SliderControl from '@/components/common/SliderControl.vue';
import DatePicker from '@/components/DatePicker';
import DownloadDialog from '@/components/DownloadDialog';
import EstateImagePicker from '@/components/estateDatabase/EstateImagePicker.vue';
import CostAndBudget from '@/components/estimates/expertEstimate/CostAndBudget';
import ExpertEstimateForm from '@/components/estimates/ExpertEstimateForm.vue';
import PredictEstimateForm from '@/components/estimates/PredictEstimateForm.vue';
import RealEstateEstimateForm from '@/components/estimates/RealEstateEstimateForm.vue';
import Icon from '@/components/Icon.vue';
import {ClassComponentInitializer, VueComponentInitializer} from '@/components/initializers';
import LazyComponent from '@/components/LazyComponent';
import LocationInput from '@/components/LocationInput.vue';
import EstatesMap from '@/components/maps/EstatesMap.vue';
import GoogleStreetView from '@/components/maps/GoogleStreetView.vue';
import MapyCzOrthoPhoto from '@/components/maps/MapyCzOrthoPhoto.vue';
import MapyCzPanorama from '@/components/maps/MapyCzPanorama.vue';
import PointsMap from '@/components/maps/PointsMap.vue';
import EnumIcon from '@/icons/EnumIcon.vue';
import ApiTransactionFinder from '@/model/cadastre/ApiTransactionFinder';
import {EventBus} from '@/model/events';
import ApiLocationFinder from '@/model/locations/ApiLocationFinder';
import BootstrapTooltip from '@/naja/BootstrapTooltip';
import {createRouterForComponent} from '@/shared/router';
import {translationsFromAttribute} from '@/shared/translations';

const provideServices = () => ({
    provide: {
        locationFinder: ApiLocationFinder.ruian(),
        eventBus: new EventBus(),
        transactionFinder: new ApiTransactionFinder(),
    },
});

const components = [
    // Vue components
    new VueComponentInitializer('#vue-real-estate-estimate-form', RealEstateEstimateForm, element => {
            const baseUri = element.getAttribute('data-base-uri');

            if (baseUri === null) {
                throw new Error('baseUri not passed to EstimateForm');
            }

            return {
                ...provideServices(),
                i18n: translationsFromAttribute(element, 'data-translations'),
                router: createRouterForComponent(element, baseUri),
            };
        },
    ),
    // Vue components
    new VueComponentInitializer('#vue-expert-estimate-form', ExpertEstimateForm, element => {
            const baseUri = element.getAttribute('data-base-uri');

            if (baseUri === null) {
                throw new Error('baseUri not passed to EstimateForm');
            }

            return {
                ...provideServices(),
                i18n: translationsFromAttribute(element, 'data-translations'),
                router: createRouterForComponent(element, baseUri),
            };
        },
    ),
    new VueComponentInitializer('#vue-predict-estimate-form', PredictEstimateForm, element => {
            const baseUri = element.getAttribute('data-base-uri');

            if (baseUri === null) {
                throw new Error('baseUri not passed to EstimateForm');
            }

            return {
                ...provideServices(),
                i18n: translationsFromAttribute(element, 'data-translations'),
                router: createRouterForComponent(element, baseUri),
            };
        },
    ),
    new VueComponentInitializer('.vue-estates-map', EstatesMap, provideServices),
    new VueComponentInitializer('.vue-location-search-ruian', LocationInput, element => {
        const locationTypes: string[] = JSON.parse(element.getAttribute('location-types') || '[]');

        if (!Array.isArray(locationTypes) || locationTypes.some(value => typeof value !== 'string')) {
            throw new Error(`Invalid value passed for locationTypes ${locationTypes.toString()}`);
        }

        return {
            provide: {
                locationFinder: ApiLocationFinder.ruian(locationTypes),
            },
        };
    }),
    new VueComponentInitializer('.vue-location-search-osm', LocationInput, element => {
        return {
            provide: {
                locationFinder: ApiLocationFinder.openStreetMap(),
            },
        };
    }),
    new VueComponentInitializer('.vue-estate-image-picker', EstateImagePicker),
    new VueComponentInitializer('.vue-slider-control', SliderControl),
    new VueComponentInitializer(
        '.vue-line-chart',
        LineChartControl,
        () => ({
            propsData: {
                language: 'cs-CZ',
            },
        }),
    ),
    new VueComponentInitializer('.vue-pie-chart', PieChartControl),
    new VueComponentInitializer('.vue-multi-select-control', MultiSelectControl),
    new VueComponentInitializer('.vue-cadastre-transaction-search', TransactionNumberInput, provideServices),
    new VueComponentInitializer('.vue-google-street-view', GoogleStreetView),
    new VueComponentInitializer('.vue-mapy-cz-panorama', MapyCzPanorama),
    new VueComponentInitializer('.vue-mapy-cz-ortophoto', MapyCzOrthoPhoto),
    new VueComponentInitializer('.vue-points-map', PointsMap, element => {
        return {
            i18n: translationsFromAttribute(element, 'data-translations'),
            provide: {
                locationFinder: ApiLocationFinder.ruian([]),
            },
        };
    }),

    // Custom components
    new ClassComponentInitializer('.lazy-dropdown', LazyComponent, 'show.bs.dropdown'),
    new ClassComponentInitializer('.lazy-collapse', LazyComponent, 'show.bs.collapse'),
    new ClassComponentInitializer('.lazy-control', LazyComponent),
    new ClassComponentInitializer('.date, [data-provide="datepicker"]', DatePicker),
    new ClassComponentInitializer('[data-download-url]', DownloadDialog),
    new ClassComponentInitializer('[data-reload-url]', ReloadContainer),
    new ClassComponentInitializer('.on-load-open-modal', AutoOpenModal),
    new ClassComponentInitializer('#cost-and-budget', CostAndBudget),
    new ClassComponentInitializer('[data-bs-toggle="tooltip"]', BootstrapTooltip),

    // Custom icons
    new VueComponentInitializer('[data-icon]:not(.svg-inline--fa)', Icon),
    new VueComponentInitializer('.enum-icon', EnumIcon),
];

export function bindInemComponents(snippet: HTMLElement): void {
    components.forEach(initializer => initializer.initialize(snippet));
}



